import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Spin,
  Alert,
  Button,
  Space,
  Typography,
  Divider,
  Tag,
  Progress,
  Table,
} from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import LibraryPage from '../../../components/layout/LibraryPage';
import { useGetResponseQuery } from '../../../store/api/templateApi';
import '../../../components/common/RichTextEditor.css';

const { Title, Text } = Typography;

export default function ResponseView() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: response, isLoading, error } = useGetResponseQuery(id);

  if (isLoading) {
    return (
      <LibraryPage title="Loading...">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <Spin size="large" />
        </div>
      </LibraryPage>
    );
  }

  if (error || !response) {
    return (
      <LibraryPage title="Error">
        <Alert
          message="Failed to load response"
          description="The response could not be loaded. Please try again."
          type="error"
          showIcon
        />
        <Button
          style={{ marginTop: 16 }}
          onClick={() => navigate('/library/completed')}
          icon={<ArrowLeftOutlined />}
        >
          Back to Completed
        </Button>
      </LibraryPage>
    );
  }

  const renderResponseData = () => {
    const responseData = response.response_data || {};
    const templateData = response.template_data || {};
    const sheets = templateData.sheets || [];

    if (sheets.length === 0) {
      return (
        <Card>
          <pre>{JSON.stringify(responseData, null, 2)}</pre>
        </Card>
      );
    }

    return sheets.map((sheet, sheetIdx) => (
      <Card key={sheetIdx} title={sheet.name} style={{ marginBottom: 16 }}>
        {sheet.sections?.map((section, sectionIdx) => (
          <div key={sectionIdx} style={{ marginBottom: 20 }}>
            <Title level={5} style={{ fontSize: '15px', marginBottom: '8px' }}>{section.name}</Title>
            <Divider style={{ margin: '4px 0 12px 0' }} />
            {section.categories?.map((category, catIdx) => (
              <div
                key={catIdx}
                style={{
                  marginBottom: 12,
                  paddingBottom: catIdx < (section.categories?.length || 0) - 1 ? 12 : 0,
                  borderBottom: catIdx < (section.categories?.length || 0) - 1 ? '1px solid #E5E7EB' : 'none'
                }}
              >
                {category.name && (
                  <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '14px', fontWeight: 600 }}>
                    {category.name}
                  </Text>
                )}
                <Descriptions bordered column={1} size="small">
                  {category.questions?.map((question, qIdx) => {
                    const answer = responseData[question.id];

                    // Render table data
                    if (question.field_type === 'table') {
                      const tableConfig = question.tableConfig || {};

                      // Row-based table (value is object)
                      if (tableConfig.orientation === 'rows' && answer && typeof answer === 'object' && !Array.isArray(answer)) {
                        return (
                          <Descriptions.Item key={qIdx} label={question.question} span={1}>
                            <div style={{ border: '1px solid #E5E7EB', borderRadius: '4px', overflow: 'hidden', marginTop: 8 }}>
                              {tableConfig.columns?.map((column, colIdx) => {
                                const val = answer[column.key];
                                let displayVal;

                                if (Array.isArray(val)) {
                                  displayVal = val.join(', ');
                                } else if (val && typeof val === 'object' && val.format) {
                                  displayVal = val.format('YYYY-MM-DD HH:mm');
                                } else if (val instanceof Date) {
                                  displayVal = val.toLocaleDateString();
                                } else if (typeof val === 'boolean') {
                                  displayVal = val ? 'Yes' : 'No';
                                } else if (val) {
                                  displayVal = String(val);
                                } else {
                                  displayVal = '—';
                                }

                                return (
                                  <div
                                    key={colIdx}
                                    style={{
                                      display: 'flex',
                                      borderBottom: colIdx < tableConfig.columns.length - 1 ? '1px solid #E5E7EB' : 'none',
                                    }}
                                  >
                                    <div style={{
                                      width: '200px',
                                      padding: '8px 12px',
                                      background: '#FAFAFA',
                                      borderRight: '1px solid #E5E7EB',
                                    }}>
                                      <Text strong style={{ fontSize: '13px' }}>{column.title}</Text>
                                    </div>
                                    <div style={{ flex: 1, padding: '8px 12px' }}>
                                      <Text style={{ fontSize: '13px' }}>{displayVal}</Text>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </Descriptions.Item>
                        );
                      }

                      // Column-based table (value is array)
                      if (Array.isArray(answer) && answer.length > 0) {
                        const columns = (tableConfig.columns || []).map(col => ({
                          title: col.title,
                          dataIndex: col.key,
                          key: col.key,
                          render: (text) => {
                            if (Array.isArray(text)) return text.join(', ');
                            if (typeof text === 'boolean') return text ? 'Yes' : 'No';
                            // Format Date objects
                            if (text && typeof text === 'object' && text.format) {
                              return text.format('YYYY-MM-DD HH:mm');
                            }
                            if (text instanceof Date) {
                              return text.toLocaleDateString();
                            }
                            return text ? String(text) : '—';
                          }
                        }));

                        return (
                          <Descriptions.Item key={qIdx} label={question.question} span={1}>
                            <Table
                              dataSource={answer}
                              columns={columns}
                              pagination={false}
                              size="small"
                              bordered
                              style={{ marginTop: 8 }}
                            />
                          </Descriptions.Item>
                        );
                      }

                      // Empty table
                      return (
                        <Descriptions.Item key={qIdx} label={question.question}>
                          <Text type="secondary">No data</Text>
                        </Descriptions.Item>
                      );
                    }

                    // Render repeater data
                    if (question.field_type === 'repeater' && Array.isArray(answer) && answer.length > 0) {
                      return (
                        <Descriptions.Item key={qIdx} label={question.question} span={1}>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            {answer.map((item, idx) => (
                              <Card key={item.key || idx} size="small" style={{ background: '#fafafa' }}>
                                <Text strong>Item {idx + 1}:</Text>
                                <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>
                                  {typeof item === 'object' ? item.data : item}
                                </div>
                              </Card>
                            ))}
                          </Space>
                        </Descriptions.Item>
                      );
                    }

                    // Render array data (multi-select, checkboxes)
                    if (Array.isArray(answer) && answer.length > 0) {
                      return (
                        <Descriptions.Item key={qIdx} label={question.question}>
                          {answer.join(', ')}
                        </Descriptions.Item>
                      );
                    }

                    // Render rich text (HTML content)
                    if (question.field_type === 'richtext' && answer) {
                      return (
                        <Descriptions.Item key={qIdx} label={question.question} span={1}>
                          <div
                            className="rich-text-content"
                            style={{
                              padding: '12px',
                              background: '#fafafa',
                              borderRadius: '4px',
                              minHeight: '60px',
                            }}
                            dangerouslySetInnerHTML={{ __html: answer }}
                          />
                        </Descriptions.Item>
                      );
                    }

                    // Render regular data
                    return (
                      <Descriptions.Item
                        key={qIdx}
                        label={question.question}
                      >
                        {answer !== undefined && answer !== null && answer !== ''
                          ? String(answer)
                          : <Text type="secondary">Not answered</Text>
                        }
                      </Descriptions.Item>
                    );
                  })}
                </Descriptions>
              </div>
            ))}
          </div>
        ))}
      </Card>
    ));
  };

  return (
    <LibraryPage
      title="Response Details"
      breadcrumbs={[
        { label: 'Library', path: '/library' },
        { label: 'Completed', path: '/library/completed' },
        { label: 'Response Details' },
      ]}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        <Card>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Header Info */}
            <div>
              <Title level={3}>{response.template_name}</Title>
              <Space size="middle" style={{ marginTop: 8 }}>
                <Tag
                  icon={response.status === 'submitted' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                  color={response.status === 'submitted' ? 'success' : 'warning'}
                >
                  {response.status === 'submitted' ? 'Submitted' : 'Draft'}
                </Tag>
                <Text type="secondary">
                  Submitted by: {response.submitted_by_name}
                </Text>
                {response.submitted_at && (
                  <Text type="secondary">
                    on {new Date(response.submitted_at).toLocaleString()}
                  </Text>
                )}
              </Space>
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">Completion: </Text>
                <Progress
                  percent={Math.round(response.completion_percentage || 0)}
                  size="small"
                  style={{ maxWidth: 200, display: 'inline-block', marginLeft: 8 }}
                />
              </div>
            </div>

            <Divider />

            {/* Response Data */}
            {renderResponseData()}

            <Divider />

            {/* Actions */}
            <Button
              onClick={() => navigate('/library/completed')}
              icon={<ArrowLeftOutlined />}
              size="large"
            >
              Back to Completed
            </Button>
          </Space>
        </Card>
      </div>
    </LibraryPage>
  );
}
